import type { FormEvent } from 'react'
import { Save } from 'lucide-react'

import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { TextArea } from '@/components/ui/TextArea'
import { Label } from '@/components/ui/Label'
import { Select } from '@/components/ui/Select'
import { Button, buttonClasses } from '@/components/ui/Button'
import { FieldError } from '@/components/feedback/FieldError'
import type { DiaryFormOptions, DiaryFormValues } from '../types'

type Props = {
  data: DiaryFormValues
  setData: <K extends keyof DiaryFormValues>(key: K, value: DiaryFormValues[K]) => void
  errors: Partial<Record<string, string>>
  processing: boolean
  options: DiaryFormOptions
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  submitLabel: string
  cancelHref: string
}

export function DiaryForm({
  data,
  setData,
  errors,
  processing,
  options,
  onSubmit,
  submitLabel,
  cancelHref,
}: Props) {
  const toggleTrainingContent = (id: string) => {
    const current = data.training_content_ids
    if (current.includes(id)) {
      setData('training_content_ids', current.filter((tid) => tid !== id))
    } else {
      setData('training_content_ids', [...current, id])
    }
  }

  const toggleDiaryTheme = (id: string) => {
    const current = data.diary_theme_ids
    if (current.includes(id)) {
      setData('diary_theme_ids', current.filter((tid) => tid !== id))
    } else {
      setData('diary_theme_ids', [...current, id])
    }
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_1fr]">
      <div className="space-y-8">
        <Card className="space-y-6">
          <h2 className="text-xl font-semibold text-white">基本情報</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="diary_date">日付</Label>
              <Input
                id="diary_date"
                type="date"
                value={data.diary_date}
                onChange={(e) => setData('diary_date', e.target.value)}
              />
              <FieldError className="mt-1" message={errors.diary_date} />
            </div>
            <div>
              <Label htmlFor="activity_type">活動種別</Label>
              <Select
                id="activity_type"
                value={data.activity_type}
                onChange={(e) => setData('activity_type', e.target.value)}
              >
                <option value="">選択してください</option>
                {options.activityTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
              <FieldError className="mt-1" message={errors.activity_type} />
            </div>
          </div>
        </Card>

        <Card className="space-y-6">
          <h2 className="text-xl font-semibold text-white">練習内容</h2>
          <p className="text-sm text-white/60">今日取り組んだ内容を選択してください（複数選択可）</p>
          <div className="flex flex-wrap gap-3">
            {options.trainingContents.map((content) => {
              const selected = data.training_content_ids.includes(content.id)
              return (
                <button
                  key={content.id}
                  type="button"
                  onClick={() => toggleTrainingContent(content.id)}
                  className={buttonClasses({ variant: selected ? 'primary' : 'secondary', size: 'sm' })}
                >
                  {content.name}
                </button>
              )
            })}
          </div>
        </Card>

        <Card className="space-y-6">
          <h2 className="text-xl font-semibold text-white">今日のテーマ</h2>
          <p className="text-sm text-white/60">意識して取り組んだテーマを選択してください（複数選択可）</p>
          <div className="flex flex-wrap gap-3">
            {options.diaryThemes.map((theme) => {
              const selected = data.diary_theme_ids.includes(theme.id)
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => toggleDiaryTheme(theme.id)}
                  className={buttonClasses({ variant: selected ? 'primary' : 'secondary', size: 'sm' })}
                >
                  {theme.name}
                </button>
              )
            })}
          </div>
        </Card>

        <Card className="space-y-6">
          <h2 className="text-xl font-semibold text-white">自己評価</h2>
          <p className="text-sm text-white/60">今日の自分を5段階で評価してください</p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <RatingField
              label="集中力"
              value={data.concentration_rating}
              onChange={(value) => setData('concentration_rating', value)}
              error={errors.concentration_rating}
            />
            <RatingField
              label="姿勢"
              value={data.attitude_rating}
              onChange={(value) => setData('attitude_rating', value)}
              error={errors.attitude_rating}
            />
            <RatingField
              label="理解度"
              value={data.understanding_rating}
              onChange={(value) => setData('understanding_rating', value)}
              error={errors.understanding_rating}
            />
            <RatingField
              label="チャレンジ"
              value={data.challenge_rating}
              onChange={(value) => setData('challenge_rating', value)}
              error={errors.challenge_rating}
            />
            <RatingField
              label="継続力"
              value={data.continuity_rating}
              onChange={(value) => setData('continuity_rating', value)}
              error={errors.continuity_rating}
            />
          </div>
        </Card>

        <Card className="space-y-6">
          <h2 className="text-xl font-semibold text-white">気づき・振り返り</h2>
          <TextArea
            rows={6}
            value={data.reflection}
            onChange={(e) => setData('reflection', e.target.value)}
            placeholder="今日の練習で気づいたこと、うまくいったこと、改善点などを自由に書いてください。"
          />
          <FieldError className="mt-1" message={errors.reflection} />
        </Card>

        <div className="flex justify-end gap-3">
          <a href={cancelHref} className={buttonClasses({ variant: 'ghost' })}>
            キャンセル
          </a>
          <Button type="submit" loading={processing}>
            <Save className="mr-2 h-4 w-4" />
            {submitLabel}
          </Button>
        </div>
      </div>

      <aside className="space-y-6">
        <Card variant="muted" className="space-y-4">
          <h3 className="font-semibold text-white">日記を書くコツ</h3>
          <ul className="space-y-3 text-sm text-white/70">
            <li>・具体的な場面を思い出しながら書きましょう</li>
            <li>・うまくいったことと改善点の両方を書くと成長につながります</li>
            <li>・継続することが大切です。短くても毎日書く習慣をつけましょう</li>
          </ul>
        </Card>
      </aside>
    </form>
  )
}

type RatingFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
}

function RatingField({ label, value, onChange, error }: RatingFieldProps) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(value === star.toString() ? '' : star.toString())}
            className={`text-2xl transition-colors ${
              parseInt(value) >= star ? 'text-yellow-400' : 'text-white/20 hover:text-white/40'
            }`}
          >
            ★
          </button>
        ))}
      </div>
      <FieldError className="mt-1" message={error} />
    </div>
  )
}
